import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, FileX } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { useBranding } from '../contexts/BrandingContext';
import { getTenantSlug } from '../lib/tenant';

type Status = 'loading' | 'not-found';

export function CmsRedirectHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const branding = useBranding();
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    const headers: Record<string, string> = { 'x-tenant-slug': getTenantSlug() };
    if (branding.defaultSpaceId) headers['x-space-id'] = branding.defaultSpaceId;

    const path = encodeURIComponent(location.pathname);

    fetch(`/customer/portal/redirect?path=${path}`, { headers })
      .then(async (res) => {
        if (!res.ok) {
          setStatus('not-found');
          return;
        }
        const { toPath } = (await res.json()) as { toPath: string; type: string };
        // For SPA redirects, treat 301 and 302 the same way:
        // external URL → replace full href, internal path → navigate
        if (toPath.startsWith('http://') || toPath.startsWith('https://')) {
          window.location.replace(toPath);
        } else {
          navigate(toPath, { replace: true });
        }
      })
      .catch(() => setStatus('not-found'));
  }, [location.pathname, branding.defaultSpaceId]);

  if (status === 'loading') {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={24} className="animate-spin text-zinc-400" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex flex-col items-center justify-center py-24 text-center gap-4">
        <FileX size={40} className="text-zinc-300" />
        <div>
          <h1 className="text-xl font-semibold text-zinc-800">Page not found</h1>
          <p className="text-sm text-zinc-500 mt-1">
            The page you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-2 text-sm font-medium text-[var(--color-primary,#4F46E5)] hover:underline"
        >
          Go to home
        </button>
      </div>
    </AppLayout>
  );
}
